// Color utility functions for map visualization

/**
 * Get color for population heatmap
 * @param {number} population - Population count
 * @returns {string} RGB color string
 */
export const getPopulationColor = (population) => {
  if (population === 0) return 'rgb(247, 251, 255)' // Very light blue
  if (population < 10000) return 'rgb(222, 235, 247)'
  if (population < 50000) return 'rgb(198, 219, 239)'
  if (population < 100000) return 'rgb(158, 202, 225)'
  if (population < 500000) return 'rgb(107, 174, 214)'
  if (population < 1000000) return 'rgb(66, 146, 198)'
  if (population < 2000000) return 'rgb(33, 113, 181)'
  if (population < 5000000) return 'rgb(8, 81, 156)'
  return 'rgb(8, 48, 107)' // Dark blue
}

/**
 * Get color for climate zones
 * @param {string} climate - Climate type
 * @returns {string} Hex color
 */
export const getClimateColor = (climate) => {
  const climateColors = {
    'tempere': '#A9E55F',
    'tropical': '#89C342',
    'aride': '#E8BD68',
    'desertique': '#FEFFB3',
    'continental': '#D3E5C9',
    'montagne': '#93408D',
    'arctique': '#6593DC'
  }

  return climateColors[climate] || '#CCCCCC'
}

/**
 * Get hemisphere color
 * @param {string} hemisphere - 'north', 'south', or 'equator'
 * @returns {string} Hex color
 */
export const getHemisphereColor = (hemisphere) => {
  const hemisphereColors = {
    'north': '#4A90E2',  // Blue
    'south': '#E24A4A',  // Red
    'equator': '#E2B54A' // Yellow/Orange
  }

  return hemisphereColors[hemisphere] || '#999999'
}

/**
 * Generate random country color (for countries without assigned colors)
 * @param {number} countryId - Country ID for consistent color generation
 * @returns {string} Hex color
 */
export const generateCountryColor = (countryId) => {
  // Use country ID to generate deterministic but varied colors
  const hue = (countryId * 137.508) % 360 // Golden angle
  const saturation = 60 + (countryId % 20)
  const lightness = 45 + (countryId % 15)
  
  return hslToHex(hue, saturation, lightness)
}

/**
 * Convert HSL to Hex color
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {string} Hex color
 */
const hslToHex = (h, s, l) => {
  l /= 100
  const a = s * Math.min(l, 1 - l) / 100
  const f = n => {
    const k = (n + h / 30) % 12
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
    return Math.round(255 * color).toString(16).padStart(2, '0')
  }
  return `#${f(0)}${f(8)}${f(4)}`
}

// ---------------------------------------------------------------------------
// Owned-country per-region statistic heatmaps
// ---------------------------------------------------------------------------

/** Color used for regions with no owned data (logged-out, or another country). */
export const STAT_NEUTRAL = '#3a3a3a'

/**
 * Heatmap color ramps for the owned-country data layers. Each ramp is a list of
 * [stopValue, color]; stops must be strictly ascending. Diverging ramps (red→green)
 * are used where "higher is better" (coverage, energy met); sequential ramps
 * (light→dark) are used for magnitudes (comfort, production, counts, km).
 */
export const STAT_RAMPS = {
  housing_coverage: [[0, '#b2182b'], [35, '#fdae61'], [70, '#a6d96a'], [100, '#1a9850']],
  comfort: [[0, '#ffffcc'], [25, '#c2e699'], [50, '#78c679'], [75, '#31a354'], [100, '#006837']],
  elec_meeting: [[0, '#b2182b'], [50, '#fdae61'], [100, '#1a9850'], [200, '#2166ac']],
  elec_production: [[0, '#fff7ec'], [1000, '#fdd49e'], [10000, '#fc8d59'], [50000, '#d7301f'], [200000, '#7f0000']],
  structures: [[0, '#eff3ff'], [1, '#bdd7e7'], [3, '#6baed6'], [6, '#3182bd'], [12, '#08519c'], [25, '#08306b']],
  infrastructure: [[0, '#edf8e9'], [50, '#bae4b3'], [200, '#74c476'], [1000, '#31a354'], [5000, '#006d2c']],
}

/**
 * Build a MapLibre `fill-color` expression for an owned-country stat layer.
 * Regions that carry the `stat_has` marker are colored along the ramp; every
 * other region falls back to the neutral color (the "detector" behavior).
 * @param {string} prop - GeoJSON feature property holding the metric
 * @param {string} rampKey - key into STAT_RAMPS
 */
export const buildStatColorExpression = (prop, rampKey) => {
  const ramp = STAT_RAMPS[rampKey] || STAT_RAMPS.structures
  const interpolate = ['interpolate', ['linear'], ['coalesce', ['to-number', ['get', prop]], 0]]
  ramp.forEach(([stop, color]) => {
    interpolate.push(stop, color)
  })
  return ['case', ['==', ['get', 'stat_has'], true], interpolate, STAT_NEUTRAL]
}

/**
 * Lighten or darken a hex color
 * @param {string} hex - Hex color
 * @param {number} percent - Percentage to lighten (positive) or darken (negative)
 * @returns {string} Modified hex color
 */
export const adjustColor = (hex, percent) => {
  const num = parseInt(hex.replace('#', ''), 16)
  const amt = Math.round(2.55 * percent)
  const R = Math.min(255, Math.max(0, (num >> 16) + amt))
  const G = Math.min(255, Math.max(0, (num >> 8 & 0x00FF) + amt))
  const B = Math.min(255, Math.max(0, (num & 0x0000FF) + amt))
  
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`
}
